import './AntdSelect.scss';

import { CloseCircleFilled } from '@ant-design/icons';
import { Button, Select } from 'antd';
import type { DefaultOptionType } from 'antd/es/select';
import { SelectProps } from 'antd/lib';
import { KeyboardEvent, useCallback, useMemo } from 'react';
import { useTranslation } from 'react-i18next';

import MobileDrawer from '@/components/general/Drawers/MobileDrawer';

import SelectOnDrawer from './SelectOnDrawer';
import { useAntdSelect } from './useAntdSelect';

export interface AntdSelectProps extends SelectProps {
  title?: string;
  drawerFullscreen?: boolean;
  drawerHeight?: number | string;
}

/**
 * @description Para reemplazar <Select> considerar que las opciones deberán estar como atributo y no como children, osea, <Select options={[]}>.
 */
const AntdSelect = (props: AntdSelectProps) => {
  const { t } = useTranslation();
  const { title, drawerFullscreen, drawerHeight, ...selectProps } = props;
  const {
    className,
    placeholder,
    mode,
    disabled,
    value,
    onChange,
    onSearch,
    filterOption,
    notFoundContent,
    allowClear = true,
    onPopupScroll,
    onKeyDown: selectOnKeyDown,
  } = selectProps;
  const { isMobile, filter, openDrawer, setOpenDrawer } = useAntdSelect(filterOption, onSearch);
  const options = useMemo(
    () =>
      props.options?.filter((option, index, self) => {
        if (option?.value === undefined || option?.value === null) {
          return true;
        }

        return self.findIndex(item => item?.value === option?.value) === index;
      }) ?? [],
    [props.options],
  );
  const optionLabelMap = useMemo(() => buildOptionLabelMap(options), [options]);
  const handleKeyDown = useCallback(
    (event: KeyboardEvent<HTMLElement>) => {
      selectOnKeyDown?.(event as KeyboardEvent<HTMLDivElement>);
      if (event.defaultPrevented || !isCopyShortcut(event)) {
        return;
      }

      const labelsToCopy = extractSelectedLabels(value, optionLabelMap);

      if (!labelsToCopy.length) {
        return;
      }

      event.preventDefault();
      copyTextToClipboard(labelsToCopy.join(', '));
    },
    [optionLabelMap, selectOnKeyDown, value],
  );

  return isMobile ? (
    <>
      <div
        className={`AntdSelectMobileElement ${!allowClear || disabled ? 'NonClear' : ''} ${className}`}>
        <Select
          {...selectProps}
          styles={{ popup: { root: { visibility: 'hidden' } } }}
          allowClear={false}
          className={`AntdSelect`}
          onKeyDown={handleKeyDown}
        />
        <div className="AntdSelectMobileTrigger" onClick={() => !disabled && setOpenDrawer(true)} />
        {allowClear && !disabled && (
          <div
            className="AntdSelectMobileClear"
            onClick={() =>
              onChange && (mode != 'multiple' ? onChange(undefined, {}) : onChange([], {}))
            }>
            <CloseCircleFilled />
          </div>
        )}
      </div>

      <MobileDrawer
        title={title ? title : placeholder}
        open={openDrawer}
        setOpen={setOpenDrawer}
        footer={
          mode == 'multiple' ? (
            <Button
              className="AntdSelectCloseDrawer"
              size="large"
              type="primary"
              onClick={() => setOpenDrawer(false)}>
              {t('Continuar')}
            </Button>
          ) : null
        }
        destroyOnHidden
        height={!drawerFullscreen ? drawerHeight : undefined}
        fullscreen={drawerFullscreen}
        onBodyScroll={onPopupScroll}>
        <SelectOnDrawer
          options={options}
          filter={filter}
          selection={value}
          mode={mode}
          notFoundContent={notFoundContent}
          onChange={onChange}
          onSearch={onSearch}
          setOpenDrawer={setOpenDrawer}
          dynamicHeight={!drawerHeight}
        />
      </MobileDrawer>
    </>
  ) : (
    <Select
      {...selectProps}
      options={options}
      filterOption={filter}
      className={`AntdSelect ${className}`}
      onKeyDown={handleKeyDown}
    />
  );
};

export default AntdSelect;

type OptionValue = string | number | null;

const normalizeLabel = (
  label: DefaultOptionType['label'],
  fallbackValue?: OptionValue,
): string | null => {
  if (label === null || label === undefined) {
    return primitiveToString(fallbackValue);
  }

  if (typeof label === 'string' || typeof label === 'number' || typeof label === 'boolean') {
    return String(label);
  }

  return primitiveToString(fallbackValue);
};

const primitiveToString = (value: unknown): string | null => {
  if (value === null || value === undefined) {
    return null;
  }

  if (typeof value === 'string' || typeof value === 'number' || typeof value === 'boolean') {
    return String(value);
  }

  return null;
};

const buildOptionLabelMap = (options?: DefaultOptionType[]) => {
  const map = new Map<OptionValue, string>();
  const walk = (opts?: DefaultOptionType[]) => {
    if (!opts) {
      return;
    }

    opts.forEach(option => {
      if (option.options?.length) {
        walk(option.options);

        return;
      }

      if (option.value === undefined) {
        return;
      }

      const label = normalizeLabel(option.label, option.value);

      if (label) {
        map.set(option.value, label);
      }
    });
  };

  walk(options);

  return map;
};

const extractSelectedLabels = (
  selection: SelectProps['value'],
  labelsByValue: Map<OptionValue, string>,
) => {
  if (selection === null || selection === undefined) {
    return [];
  }

  const selectionArray = Array.isArray(selection) ? selection : [selection];

  return selectionArray.reduce<string[]>((labels, item) => {
    const label = resolveLabel(item, labelsByValue);

    if (label) {
      labels.push(label);
    }

    return labels;
  }, []);
};

const resolveLabel = (
  selectionItem: SelectProps['value'],
  labelsByValue: Map<OptionValue, string>,
): string | null => {
  if (selectionItem === null || selectionItem === undefined) {
    return null;
  }

  if (typeof selectionItem === 'object') {
    const labeledValue = selectionItem as LabeledValue;
    const directLabel = normalizeLabel(labeledValue.label, labeledValue.value);

    if (directLabel) {
      return directLabel;
    }

    return labelsByValue.get(labeledValue.value ?? null) ?? null;
  }

  return labelsByValue.get(selectionItem ?? null) ?? primitiveToString(selectionItem);
};

const copyTextToClipboard = (text: string) => {
  if (!text) {
    return;
  }

  if (typeof navigator !== 'undefined' && navigator.clipboard?.writeText) {
    navigator.clipboard.writeText(text).catch(() => fallbackCopy(text));

    return;
  }

  fallbackCopy(text);
};

const fallbackCopy = (text: string) => {
  if (typeof document === 'undefined') {
    return;
  }

  const textarea = document.createElement('textarea');

  textarea.value = text;
  textarea.style.position = 'fixed';
  textarea.style.top = '0';
  textarea.style.left = '0';
  textarea.style.width = '1px';
  textarea.style.height = '1px';
  textarea.style.opacity = '0';
  document.body.appendChild(textarea);
  textarea.focus();
  textarea.select();

  try {
    document.execCommand('copy');
  } finally {
    document.body.removeChild(textarea);
  }
};

const isCopyShortcut = (event: KeyboardEvent<HTMLElement>) =>
  (event.ctrlKey || event.metaKey) && event.key.toLowerCase() === 'c';

interface LabeledValue {
  value?: OptionValue;
  label?: DefaultOptionType['label'];
}
